import React, { useState, useEffect } from "react";
import { SENSAIPageContext } from "../types";

interface ActivityTabProps {
  context: SENSAIPageContext;
}

interface ActivityItem {
  id: string;
  type: "teach" | "verify" | "action";
  description: string;
  timestamp: string;
  result?: "success" | "failure";
}

export const ActivityTab: React.FC<ActivityTabProps> = ({ context }) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadActivity = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/sensai/activity', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setActivities(data.activities || []);
        }
      } catch (error) {
        console.error("Failed to load activity:", error);
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-slate-400">Loading activity...</div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center text-slate-400">
        <div className="text-sm">No recent SENSAI activity.</div>
        <div className="text-xs mt-2">
          Use Teach or Verify tabs to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-white text-sm">Recent Activity</h3>
      
      <div className="space-y-4">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className="sensai-card p-4 rounded-xl"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="text-lg">
                  {activity.type === "teach" && "🎯"}
                  {activity.type === "verify" && "🔍"}
                  {activity.type === "action" && "⚡"}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white text-sm font-semibold">
                      {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                    </span>
                    {activity.result && (
                      <span className={`text-xs ${
                        activity.result === "success" ? "text-green-400" : "text-red-400"
                      }`}>
                        {activity.result === "success" ? "✅" : "❌"}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-300 text-xs leading-relaxed">{activity.description}</p>
                  <div className="text-slate-400 text-xs mt-2 font-medium">
                    {new Date(activity.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
